/* NanoGrow Max: product page purchase section (gallery + option picker + sticky bar sync).
   Loaded as a deferred module by sections/main-product.liquid. The Ajax add itself lives in global.js. */

const money = (cents) =>
  window.NGM?.money ? window.NGM.money(cents) : `$${((Number(cents) || 0) / 100).toFixed(2)}`;

/* ---------------- Gallery: scroll-snap track + dots + thumbs ---------------- */
class NGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    const dotsWrap = this.querySelector('[data-dots]');
    if (!this.track || this.slides.length < 2) {
      this.classList.add('is-single');
      return;
    }
    this.dots = this.slides.map((_, i) => {
      const d = document.createElement('span');
      d.className = 'n-gallery__dot';
      if (i === 0) d.classList.add('is-active');
      dotsWrap?.appendChild(d);
      return d;
    });
    this.thumbs.forEach((t) =>
      t.addEventListener('click', () => this.go(Number(t.dataset.thumb))),
    );
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
    });
    this.index = 0;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting && en.intersectionRatio > 0.55) this.setActive(this.slides.indexOf(en.target));
        });
      },
      { root: this.track, threshold: [0.55, 0.9] },
    );
    this.slides.forEach((s) => io.observe(s));
  }
  go(i) {
    const n = Math.max(0, Math.min(this.slides.length - 1, i));
    const slide = this.slides[n];
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    this.setActive(n);
  }
  setActive(i) {
    if (i < 0 || i === this.index) return;
    this.index = i;
    this.dots?.forEach((d, k) => d.classList.toggle('is-active', k === i));
    this.thumbs.forEach((t, k) => {
      if (k === i) {
        t.setAttribute('aria-current', 'true');
        t.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      } else t.removeAttribute('aria-current');
    });
    // Pause videos that scrolled away
    this.slides.forEach((s, k) => { if (k !== i) s.querySelector('video')?.pause(); });
  }
}
customElements.define('n-gallery', NGallery);

/* ---------------- Purchase form ---------------- */
class NPdp extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[data-ngm-add]');
    if (!this.form) return;
    this.idInput = this.form.querySelector('[data-id-input]');
    this.qty = this.form.querySelector('[data-qty]');
    this.$ = (sel) => this.querySelector(sel);
    this.sticky = this.$('[data-sticky-atc]');

    this.form.addEventListener('change', (e) => {
      if (e.target.matches('[data-choice]')) this.update(true);
      if (e.target === this.qty) this.update(false);
    });
    this.form.addEventListener('click', (e) => {
      const step = e.target.closest('[data-qty-step]');
      if (!step) return;
      const next = Math.min(99, Math.max(1, (Number(this.qty.value) || 1) + Number(step.dataset.qtyStep)));
      this.qty.value = next;
      this.update(false);
    });
    // Any [data-ngm-select="bundle|gromax|rootmax"] link on the page selects that card instead of navigating.
    document.addEventListener('click', (e) => {
      const t = e.target.closest('[data-ngm-select]');
      if (!t) return;
      const input = this.form.querySelector(`[data-choice][data-key="${t.dataset.ngmSelect}"]:not(:disabled)`);
      if (!input) return;
      e.preventDefault();
      input.checked = true;
      this.update(true);
      if (!this.contains(t)) {
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.form.querySelector('[data-picker]')?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      }
      input.focus({ preventScroll: true });
    });
    // The sticky bar is hidden with aria-hidden by global.js; keep its button out of the tab order too.
    if (this.sticky) {
      new MutationObserver(() => {
        this.sticky.inert = this.sticky.getAttribute('aria-hidden') === 'true';
      }).observe(this.sticky, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
    this.update(false);
  }

  get choice() {
    return this.form.querySelector('[data-choice]:checked') || this.form.querySelector('[data-choice]');
  }

  update(userAction) {
    const c = this.choice;
    if (!c) return;
    const d = c.dataset;
    const price = Number(d.price);
    const compare = Number(d.compare);
    const qty = Math.max(1, Number(this.qty?.value) || 1);
    const available = d.available === 'true';
    const save = compare > price ? compare - price : 0;

    this.idInput.value = c.value;
    const set = (sel, text) => this.querySelectorAll(sel).forEach((el) => { el.textContent = text; });
    set('[data-price]', money(price));
    set('[data-atc-price]', money(price * qty));
    set('[data-sticky-price]', money(price * qty));
    const was = this.$('[data-compare]');
    if (was) {
      was.hidden = !save;
      if (save) was.lastChild.textContent = money(compare);
    }
    const saveEl = this.$('[data-save]');
    if (saveEl) { saveEl.hidden = !save; set('[data-save-amount]', money(save * qty)); }

    // ATC state
    const atc = this.$('[data-atc]');
    const label = this.$('[data-atc-label]');
    const stickyBtn = this.$('[data-sticky-btn]');
    [atc, stickyBtn].forEach((b) => b && (b.disabled = !available));
    if (label) label.textContent = available ? (label.dataset.label ||= label.textContent) : 'Sold out';

    // Conditional blocks (gift banner shows only with the bundle), nudge for singles
    this.querySelectorAll('[data-show-when]').forEach((el) => { el.hidden = el.dataset.showWhen !== d.key; });
    const nudge = this.$('[data-nudge]');
    if (nudge) nudge.hidden = d.key === 'bundle';
    // Shop Pay installments only know this product's variants
    const terms = this.$('[data-terms]');
    if (terms) terms.hidden = d.current !== 'true';

    // Sticky bar mirrors the selection
    set('[data-sticky-title]', d.title || '');
    if (d.meta) set('[data-sticky-meta]', d.meta);
    const thumbWrap = this.$('[data-sticky-thumb-wrap]');
    if (thumbWrap && d.thumb) {
      let img = thumbWrap.querySelector('img');
      if (!img) { img = document.createElement('img'); img.alt = ''; img.width = 44; img.height = 44; thumbWrap.appendChild(img); }
      if (img.getAttribute('src') !== d.thumb) { img.removeAttribute('srcset'); img.src = d.thumb; }
    }

    if (userAction) {
      if (this.dataset.mode === 'tiles') {
        const url = new URL(location.href);
        url.searchParams.set('variant', c.value);
        history.replaceState(history.state, '', url);
      }
      window.NGM?.track?.('select_option', { key: d.key, variant: c.value, price });
      const live = document.querySelector('[data-live-region]');
      if (live) live.textContent = `${d.title}, ${money(price)}`;
    }
  }
}
customElements.define('n-pdp', NPdp);

/* Gallery fit: a small blurred copy of each photo fills the square frame behind the contained photo (see pdp.css). */
document.querySelectorAll('.n-gallery__slide').forEach((slide) => {
  const img = slide.querySelector('img.n-gallery__img');
  if (!img || slide.querySelector('.n-gallery__bg')) return;
  const bg = document.createElement('img');
  bg.className = 'n-gallery__bg';
  bg.alt = '';
  bg.setAttribute('aria-hidden', 'true');
  bg.decoding = 'async';
  bg.src = img.src.replace(/([?&])width=\d+/, '$1width=120');
  slide.prepend(bg);
});
